import * as React from 'react';

import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getFormKey } from '../helpers/getFormKey';
import { InputProps } from '../types';

export const MultipleChoiceInput: React.FC<React.PropsWithChildren<InputProps>> = ({
  autofocus,
  field,
  onFocus,
  register,
  error,
  control
}) => {
  const { id, required, options, other, none_of_above } = field;

  const { t } = useTranslation('SurveyQuestion');

  const name = id.toString();
  const labelPath = getFormKey(field); // "874.label"
  const otherPath = `${name}.other`; // "874.other"

  return (
    <fieldset>
      <legend className='sr-only'>
        {field.label}, {field.helper}
      </legend>

      <Controller
        name={labelPath}
        control={control}
        defaultValue={[]}
        render={({ onChange, value = [] }) => {
          const handleCheckboxChange = (option: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const isChecked = e.target.checked;
            let updated = [...value];

            if (isChecked) {
              updated = updated.filter((v) => v !== 'None of the above');
              if (!updated.includes(option)) updated.push(option);
            } else {
              updated = updated.filter((v) => v !== option);
            }

            onChange(updated);
          };

          const handleNoneOfAboveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.checked) {
              onChange(['None of the above']);
            }
          };

          return (
            <>
              {options?.filter(Boolean).map((o, i) => (
                <div key={o} className='flex flex-nowrap items-center'>
                  <input
                    id={`${name}-${o}`}
                    type='checkbox'
                    className='focus-ring-custom-brand input-custom-brand mr-2 mt-0.5 h-4 w-4 border-gray-400'
                    name={name}
                    value={o}
                    autoFocus={i === 0 && autofocus}
                    aria-invalid={error ? 'true' : 'false'}
                    aria-required={required ? 'true' : 'false'}
                    aria-labelledby={`${name}-${o}`}
                    onChange={handleCheckboxChange(o)}
                    checked={value.includes(o)}
                  />
                  <label
                    id={`${name}-${o}`}
                    htmlFor={`${name}-${o}`}
                    onFocus={onFocus}
                    className='flex cursor-pointer py-2 text-base font-normal normal-case'
                  >
                    {o}
                  </label>
                </div>
              ))}

              {other && (
                <div className='flex flex-nowrap items-center'>
                  <input
                    type='checkbox'
                    className='focus-ring-custom-brand input-custom-brand mr-2 mt-0.5 h-4 w-4 border-gray-400'
                    name={name}
                    value='other'
                    id={`${name}-other`}
                    aria-invalid={error ? 'true' : 'false'}
                    onChange={handleCheckboxChange('other')}
                    checked={value.includes('other')}
                  />
                  <label
                    htmlFor={`${name}-other`}
                    id={`${name}-other`}
                    onFocus={onFocus}
                    className='flex cursor-pointer py-2 text-base font-normal normal-case'
                  >
                    {t('other')}
                  </label>
                </div>
              )}

              {value.includes('other') && (
                <input
                  type='text'
                  className='rounded-d mt-2 block w-full border border-gray-200 px-10 py-3'
                  placeholder={t('please_specify')}
                  onFocus={onFocus}
                  autoFocus
                  name={otherPath}
                  ref={register({ required: required || value.includes('other') })}
                  aria-invalid={error ? 'true' : 'false'}
                />
              )}

              {none_of_above && (
                <>
                  <div className='my-3 w-full border-t border-gray-200' />
                  <div className='flex flex-nowrap items-center'>
                    <input
                      type='radio'
                      className='focus-ring-custom-brand input-custom-brand mr-2 h-4 w-4 border-gray-400'
                      name={`${name}_none_radio`} // not tracked by RHF
                      value='None of the above'
                      id={`${name}-none_of_above`}
                      onChange={handleNoneOfAboveChange}
                      checked={value.includes('None of the above')}
                      aria-checked={value.includes('None of the above')}
                      aria-describedby={`description-for-${id}`}
                      aria-labelledby={`${name}-none_of_above`}
                    />
                    <label
                      htmlFor={`${name}-none_of_above`}
                      id={`${name}-none_of_above`}
                      onFocus={onFocus}
                      className='flex cursor-pointer py-2 text-base font-normal normal-case'
                    >
                      {t('none_of_above')}
                    </label>
                  </div>
                </>
              )}
            </>
          );
        }}
      />
    </fieldset>
  );
};
